import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Globe, Radar, MessagesSquare, Briefcase, Star, ShieldAlert, Crosshair,
  CalendarDays, Newspaper, GraduationCap, Landmark, TrendingUp,
  ArrowRight, CheckCircle2, AlertTriangle, Database, Sparkles, MessageSquareText,
} from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";

const MODULES = [
  { icon: Radar, name: "Market Scanner", route: "/scanner", desc: "Screen 70+ US & UK large caps with preset or custom criteria — every match explains why it qualified." },
  { icon: MessagesSquare, name: "AI Research Assistant", route: "/assistant", desc: "Ask questions in plain English; answers grounded in real cached fundamentals, technicals and news." },
  { icon: Briefcase, name: "Portfolio Tracker", route: "/portfolio", desc: "Track lots and P/L with proper UK pence handling — and ask \u201cwhy did my portfolio fall today?\u201d" },
  { icon: Star, name: "Watchlists & Alerts", route: "/watchlists", desc: "Follow candidates with price and RSI alerts, plus AI theses on what to watch next." },
  { icon: ShieldAlert, name: "Risk Tools", route: "/risk", desc: "Concentration warnings, volatility, beta and a diversification score for your holdings." },
  { icon: Crosshair, name: "Stop-loss Calculator", route: "/risk#stoploss", desc: "Fixed, ATR-based and support-level stops with position sizing from your risk budget." },
  { icon: CalendarDays, name: "Dividend Calendar", route: "/dividends", desc: "Upcoming ex-dividend dates and a projection of your annual income — real data, honest gaps." },
  { icon: Newspaper, name: "Economic News Analysis", route: "/macro", desc: "Macro dashboard — indices, VIX, rates, FX — plus an AI daily briefing grounded in headlines." },
  { icon: GraduationCap, name: "Learning Centre", route: "/learn", desc: "Twelve plain-English lessons with quizzes and an AI tutor, from foundations to UK essentials." },
  { icon: Landmark, name: "ISA & UK Tax Assistant", route: "/tax", desc: "Allowance tracker and dividend/CGT estimators for the current tax year. Educational, not advice." },
  { icon: TrendingUp, name: "Wealth Simulator", route: "/simulator", desc: "Compound growth projections with Monte Carlo outcome bands — illustrations, not forecasts." },
];

const STEPS = [
  { icon: Database, num: "01", title: "Real market data", desc: "Quotes, fundamentals, technical indicators and news headlines, cached from public market sources across the research universe." },
  { icon: Sparkles, num: "02", title: "Grounded AI analysis", desc: "The AI reads only that data — valuations against sector peers, trend and momentum signals, and what the headlines say." },
  { icon: MessageSquareText, num: "03", title: "Explanations, not predictions", desc: "You get attractive factors and risk factors in plain English. Never a buy or sell instruction, never a price target promise." },
];

const Brand = () => (
  <span className="flex items-center gap-2">
    <span className="w-7 h-7 bg-primary flex items-center justify-center rounded-sm">
      <Globe className="w-4 h-4 text-white" />
    </span>
    <span className="font-heading font-black tracking-tighter text-lg text-white">
      SphereInvest<span className="text-primary"> AI</span>
    </span>
  </span>
);

const ExampleSummaryCard = () => (
  <div className="widget p-6 text-left" data-testid="landing-example-card">
    <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
      <span className="overline-label text-primary">AI Research Summary</span>
      <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-yellow-500/40 text-yellow-500 rounded-sm">
        Example output
      </span>
    </div>
    <div className="font-heading font-bold text-white tracking-tight mb-4">
      Illustrative Large-Cap plc <span className="font-mono text-xs text-muted-foreground">· Consumer Defensive</span>
    </div>
    <div className="grid sm:grid-cols-2 gap-5">
      <div>
        <div className="overline-label text-gain mb-2">Attractive factors</div>
        <ul className="space-y-2">
          {["Trades below its sector's average PE while margins have held steady",
            "Dividend covered by earnings, with a decade of uninterrupted payments",
            "Price above both 50- and 200-day averages — trend classified as uptrend"].map((t, i) => (
            <li key={i} className="flex gap-2 text-sm text-gray-300 leading-snug">
              <CheckCircle2 className="w-3.5 h-3.5 text-gain shrink-0 mt-0.5" /> {t}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="overline-label text-loss mb-2">Risk factors</div>
        <ul className="space-y-2">
          {["Revenue growth has slowed for two consecutive quarters",
            "Debt-to-equity sits above the peer average, limiting flexibility",
            "Recent headlines flag pricing pressure in its core market"].map((t, i) => (
            <li key={i} className="flex gap-2 text-sm text-gray-300 leading-snug">
              <AlertTriangle className="w-3.5 h-3.5 text-loss shrink-0 mt-0.5" /> {t}
            </li>
          ))}
        </ul>
      </div>
    </div>
    <p className="text-sm text-gray-400 leading-relaxed mt-5 pt-4 border-t border-white/10">
      <span className="text-white font-medium">Valuation view:</span> cheaper than peers on earnings, in line on
      dividend yield — the discount reflects slower growth rather than obvious mispricing.
    </p>
    <p className="text-xs font-mono text-muted-foreground mt-4">
      This is AI-generated research, not financial advice.
    </p>
  </div>
);

export default function Landing() {
  const [featuredId, setFeaturedId] = useState(null);

  useEffect(() => {
    api.get("/share/featured").then((r) => setFeaturedId(r.data.id)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-white" data-testid="landing-page">
      {/* Top nav */}
      <header className="sticky top-0 z-40 border-b border-white/10 backdrop-blur-xl bg-black/60">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Brand />
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="landing-nav-signin">
              <Button variant="ghost" className="rounded-sm text-muted-foreground hover:text-white">Sign in</Button>
            </Link>
            <Link to="/login?mode=register" data-testid="landing-nav-register">
              <Button className="rounded-sm bg-primary hover:bg-primary/85 text-white">Get started free</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-16 lg:pt-28 lg:pb-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <div className="overline-label mb-4">AI Investment Research · US &amp; UK large caps</div>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
            Research that explains <span className="text-primary">why</span> — never &ldquo;buy this&rdquo;.
          </h1>
          <p className="text-base text-gray-300 leading-relaxed mt-6 max-w-xl">
            SphereInvest AI reads real fundamentals, technical signals, peer valuations and news across 70+ US &amp; UK
            large caps — then explains why a stock matches your criteria, and where the risks hide. Analysis, never
            instructions.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/login?mode=register" data-testid="landing-hero-register">
              <Button className="rounded-sm bg-primary hover:bg-primary/85 text-white h-12 px-7 text-base gap-2">
                Get started free <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/login" data-testid="landing-hero-signin">
              <Button variant="outline" className="rounded-sm border-border h-12 px-7 text-base text-gray-200 hover:text-white">
                Sign in
              </Button>
            </Link>
            {featuredId && (
              <Link to={`/share/${featuredId}`} data-testid="landing-live-example">
                <Button variant="outline"
                  className="rounded-sm border-primary/50 text-primary hover:text-white hover:bg-primary/15 h-12 px-7 text-base gap-2">
                  <Sparkles className="w-4 h-4" /> See a live example
                </Button>
              </Link>
            )}
          </div>
          {featuredId && (
            <p className="text-xs font-mono text-primary/80 mt-3" data-testid="landing-live-example-caption">
              Real AI research output — no account needed.
            </p>
          )}
          <p className="text-xs font-mono text-muted-foreground mt-6">
            AI-generated research · Not financial advice · Educational purpose
          </p>
        </div>

        {/* Illustrative scanner preview */}
        <div className="widget p-5 fade-up" data-testid="landing-hero-preview">
          <div className="flex items-center justify-between mb-3">
            <span className="overline-label">Market Scanner · match reasons</span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-yellow-500">Illustrative preview</span>
          </div>
          {[
            { t: "Undervalued Quality", r: "PE 32% below sector average with double-digit ROE and low leverage" },
            { t: "Dividend Income", r: "4.1% yield with payout comfortably covered by earnings" },
            { t: "Growth Momentum", r: "Revenue accelerating; price above rising 50- and 200-day averages" },
            { t: "Oversold Bounce", r: "RSI dipped under 35 while the longer-term uptrend stayed intact" },
          ].map((row, i) => (
            <div key={i} className="flex items-start gap-3 px-3 py-3 even:bg-white/[0.02] border-l-2 border-primary/50">
              <span className="font-mono text-xs text-primary whitespace-nowrap mt-0.5 w-36 shrink-0">{row.t}</span>
              <span className="text-sm text-gray-300 leading-snug">{row.r}</span>
            </div>
          ))}
          <p className="text-[11px] font-mono text-muted-foreground mt-3">
            Every screen result carries a plain-English reason it matched.
          </p>
        </div>
      </section>

      {/* Modules */}
      <section className="border-t border-border bg-card/40">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="overline-label mb-3">Eleven research modules, one terminal</div>
          <h2 className="font-heading font-bold text-lg md:text-lg text-white tracking-tight mb-10 max-w-2xl">
            From screening to stop-losses to UK tax basics — everything a self-directed investor researches, in one place.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="landing-modules-grid">
            {MODULES.map((m) => {
              const Icon = m.icon;
              return (
                <Link key={m.name} to={m.route}
                  className="widget p-5 group block cursor-pointer hover:border-primary/60 active:border-primary active:bg-white/[0.03] transition-colors duration-150"
                  data-testid={`module-card-${m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="flex items-start justify-between">
                    <Icon className="w-5 h-5 text-primary mb-3" />
                    <ArrowRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary transition-colors duration-150" />
                  </div>
                  <div className="font-heading font-bold text-white tracking-tight mb-1.5">{m.name}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-6 py-20" data-testid="landing-how-it-works">
        <div className="overline-label mb-3">How it works</div>
        <h2 className="font-heading font-bold text-lg text-white tracking-tight mb-10">
          Grounded in data at every step — so the AI can show its working.
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.num} className="widget p-6">
                <div className="flex items-center justify-between mb-4">
                  <Icon className="w-5 h-5 text-primary" />
                  <span className="font-mono text-3xl font-medium text-white/10">{s.num}</span>
                </div>
                <div className="font-heading font-bold text-white tracking-tight mb-2">{s.title}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Example output */}
      <section className="border-t border-border bg-card/40">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="landing-example-output">
          <div className="overline-label mb-3">The product's voice</div>
          <h2 className="font-heading font-bold text-lg text-white tracking-tight mb-10">
            What a research summary looks like — balanced, specific, and always labelled as analysis.
          </h2>
          <ExampleSummaryCard />
        </div>
      </section>

      {/* Trust / compliance */}
      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="landing-compliance-strip">
        <div className="widget p-8 border-yellow-500/30">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ["Not financial advice", "Everything here is AI-generated research for educational purposes. It is never a recommendation to buy or sell anything."],
              ["Criteria, not tips", "The platform tells you which companies match the criteria you selected — and why — not what will \u201cgo up\u201d."],
              ["Grounded and transparent", "Analysis is built only from cached public market data, and every AI output carries a visible disclaimer."],
            ].map(([t, d]) => (
              <div key={t}>
                <div className="font-heading font-bold text-white tracking-tight mb-2">{t}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="text-center mt-14">
          <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-4xl text-white mb-6">
            Start researching in minutes.
          </h2>
          <Link to="/login?mode=register" data-testid="landing-bottom-register">
            <Button className="rounded-sm bg-primary hover:bg-primary/85 text-white h-12 px-8 text-base gap-2">
              Create your free account <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-6 py-10 space-y-5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Brand />
            <nav className="flex flex-wrap gap-x-5 gap-y-1 text-xs font-mono text-muted-foreground" data-testid="landing-footer-links">
              <Link to="/methodology" className="hover:text-white">Methodology</Link>
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <Link to="/disclaimer" className="hover:text-white">Disclaimer</Link>
            </nav>
          </div>
          <p className="text-[11px] font-mono text-muted-foreground/70 leading-relaxed max-w-3xl" data-testid="landing-compliance-statement">
            SphereInvest AI is an investment research and analytical technology platform. It does not execute trades,
            hold client funds or provide personalised financial advice. Information provided through the platform is
            for research and educational purposes only. Capital at risk: the value of investments can fall as well as
            rise.
          </p>
          <p className="text-[11px] font-mono text-muted-foreground/60 leading-relaxed" data-testid="landing-operator-statement">
            SphereInvest AI is operated by Three Spheres Healthcare Limited, a company registered in England and Wales
            (Company No. 14029879). Contact:{" "}
            <a href="mailto:info@threesphereshealthcare.org" className="hover:text-white">info@threesphereshealthcare.org</a>.
          </p>
          <p className="text-[11px] font-mono text-muted-foreground/70">
            Research insights — built to support, not replace, your own decisions. © {new Date().getFullYear()} SphereInvest AI.
          </p>
        </div>
      </footer>
    </div>
  );
}
